"use client";
import type { RowData } from "@tanstack/react-table";

import { useRef, type UIEvent } from "react";
import { cn } from "@/lib/utils";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useMobileMatchMedia } from "@/hooks/use-mobile";
import { RecordRowMenu } from "./record-row-menu";
import { useInfiniteScrollSentinel } from "./use-infinite-scroll-sentinel";
import { InfiniteScrollFooter } from "./infinite-scroll-footer";
import type { RecordGridProps } from "./record-grid-props";
import {
  RecordGridDesktopTable,
  RecordGridMobileView,
} from "./record-grid-views";
import { useRecordGridModel } from "./use-record-grid-model";

export function RecordGrid<TData extends RowData>(props: RecordGridProps<TData>) {
  const { data, rowActions, infiniteScroll, canLoadMore, onLoadMore } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const viewportIsMobile = useMobileMatchMedia();
  const sentinelRef = useInfiniteScrollSentinel({
    enabled: Boolean(infiniteScroll),
    canLoadMore,
    onLoadMore,
  });
  const grid = useRecordGridModel(props, scrollRef);
  const { rows, isLoading, rowMenu } = grid;

  return (
    <DndContext
      sensors={grid.sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={grid.handleDragEnd}
    >
      <div
        ref={scrollRef}
        aria-busy={isLoading}
        onScroll={markHorizontalScroll}
        className={cn("min-h-0 flex-1 overflow-auto in-data-[page-fill]:overscroll-contain", rows.length === 0 && !isLoading && "flex flex-col")}
      >
        {viewportIsMobile === false ? null : (
          <RecordGridMobileView props={props} grid={grid} />
        )}
        {viewportIsMobile === true ? null : (
          <RecordGridDesktopTable props={props} grid={grid} />
        )}
        <RecordRowMenu {...rowMenu} actions={rowActions} />
        {infiniteScroll ? (
          <InfiniteScrollFooter
            sentinelRef={sentinelRef}
            hasRows={rows.length > 0}
            isLoadingMore={
              data.mode === "cursor"
                ? Boolean(data.isLoadingMore)
                : Boolean(data.isLoading)
            }
            canLoadMore={canLoadMore}
            onLoadMore={onLoadMore}
          />
        ) : null}
      </div>
    </DndContext>
  );
}

function markHorizontalScroll(event: UIEvent<HTMLDivElement>) {
  const scroller = event.currentTarget;
  scroller.toggleAttribute("data-scrolled-x", scroller.scrollLeft > 0);
}
